export interface SimpleOption {
	label: string;
	value: string;
}

export interface DedobUser extends WithPermissions {
	username: string;
	email: string;
	isSuperUser: boolean;
	version: string;
}

export enum Permissions {
	change = 'change',
	delete = 'delete',
	clear = 'clear',
	submitAndClear = 'submit&clear',
	all = 'all',
	reject = 'reject',
	create = 'create',
	publish = 'publish',
	view = 'view',
	submit = 'submit',

	addSection = 'addSection',
	clearSection = 'clearSection',
	submitAndClearSection = 'submit&clearSection',
	publishSection = 'publishSection',
	createRevision = 'createRevision',
}

export enum EditionState {
	current = 'current',
	published = 'published',
}

export enum SectionState {
	new = 'new',
	drafted = 'drafted',
	cleared = 'cleared',
	submitted = 'submitted',
	published = 'published',
}

export enum Topic {
	highlights = 'HL',

	l3emergency = 'L3',
	l2emergency = 'L2',
	monitoring = 'M',

	redAlert = 'RA',
	orangeAlert = 'OA',
	yellowAlert = 'YA',

	other = 'OT',
	naturalHazard = 'NH',

	// from Jan 2022
	scaleUp = 'SU',
	corporateAttention = 'CA',
	actionResponse = 'AR',

	// from Jan 2026
    corporatePreparedness = 'CP',
    corporateScaleUp = 'CSU',
    corporateFocus = 'CF',
}


export const HighlightsTitle = 'Highlights';

export const topicLabels = {
	[Topic.highlights]: 'Highlights',

	[Topic.l3emergency]: 'L3 emergency',
	[Topic.l2emergency]: 'L2 emergency',
	[Topic.monitoring]: 'Monitoring',

	[Topic.redAlert]: 'Red alert',
	[Topic.orangeAlert]: 'Orange alert',
	[Topic.yellowAlert]: 'Yellow alert',

	[Topic.other]: 'Other',
	[Topic.naturalHazard]: 'Natural hazard',

	// from Jan 2022
	[Topic.scaleUp]: 'Corporate Scale-Up',
	[Topic.corporateAttention]: 'Corporate Attention',
	[Topic.actionResponse]: 'Early Action & Emergency Response',

	// from Jan 2026
	[Topic.corporatePreparedness]: 'Corporate Preparedness',
	[Topic.corporateScaleUp]: 'Corporate Scale-Up',
	[Topic.corporateFocus]: 'Corporate Focus',
};

export interface Country {
	pk: number;
	name: string;
}

export enum Confidentiality {
	confidential = 'Confidential',
}

export interface WithPermissions {
	permissions: Permissions[],
}

export function hasPermissions(data: WithPermissions, perm: Permissions | Permissions[]) {
	return (Array.isArray(perm) ? perm : [perm])
		.some(p => data.permissions.includes(p));
}

export interface CreateSectionData {
	slug: string;
	topicId: number;
	sub: number;
	title: string;
	isHighlights?: boolean;
}

export const sectionActionReject = 'reject';

export const topicsToOrder = [Topic.l3emergency, Topic.l2emergency, Topic.monitoring];
export const orderInDocNotAssigned = 1000;

export interface Section extends WithPermissions {
	id: number,
	edition: number,  // id of container edition
	topic: Topic,
	state: SectionState,
	stateLabel: string,
	lastAuthor: string,
	lastAction: string,
	title: string,
	subtitle: string,
	countries: Country[],
	content: string,
	confidentiality: Confidentiality,
	// "createdOn": "2021-04-28T10:08:25.165723+02:00",
	image: null | {
		id: number,
		title: string,
		file: string,
		// isLocal: boolean,
		// link: string,
	},
	// tables: [],
	// lastAction: "create",
	tags: string[],
	owner: string,
	// user: string,
	// order: number,
	orderInDoc: number,
}

export interface CreatedSection {
	sectionId: number,
	crisisId: number,
}

export enum SectionAction {
	delete = 'delete',
	submit = 'submit',
	clear = 'clear',
	reject = 'reject',
}

export interface SectionVersion {
	state: SectionState,
	title: string,
	subtitle: string,
	content: string,
	author: string,
	date: string,  // timestamp like 2021-11-10T09:56:48.190000Z
}

export interface TopicSection extends WithPermissions {
	id: number,
	topic: Topic,
	title: string,
	subtitle: string,
	contentSet: Section[];
}

export interface EditionDef {
	name: string,
}

export interface EmailRecipients {
	to: string[],
	cc: string[],
	bcc: string[],
}

export interface Edition {
	id: number,
	name: string,
	docSlug: string,
	state: EditionState,
	topicSectionSet: TopicSection[],
	addresses: EmailRecipients,
	type: { name: string },
	issue: { sent: boolean, scheduledSend: string } | null,
}

export interface Template {
	header: string;
	footer: string;
}

export interface Document {
	slug: string;  // this is the docId
	editionSet: Edition[];
	isNewDocument: boolean;
	draftingCanStart: boolean;
}

export interface TopicTitle {
	mainTopicId: number,
	id: number,
	crisisId: number,
	title: string,
	drafter: string,
}

export interface TopicInfo {
	id: number;
	topic: Topic;
	order: number;
	titles: TopicTitle[];
	feCleanTitles?: string[];
}

export interface OpUpdate {
	country: number;
	date: string;
	updatedOn: string;
	title: string;
	description: string;
}